import { useCallback, useMemo } from 'react';
import {
  qNoOp,
  qUserUiPreference,
  userUiPreferenceId,
  type UserUiPreferenceRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../adapters/powersync';

/**
 * PRD.300 — reusable read/write access to one `user_ui_preferences` row,
 * scoped to the active org + signed-in user.
 *
 * The stored value is opaque JSON, so any future UI preference reuses this hook
 * with its own `preferenceKey` instead of growing another settings table.
 *
 * `raw` is `null` when the user has NO saved preference — a meaningful state,
 * not an error: it means "follow the clinic default". `clear()` returns the user
 * to that state, which is why Reset deletes rather than writing a snapshot.
 */
export function useUserUiPreference(
  orgId: string,
  userId: string,
  preferenceKey: string,
): {
  /** The raw `value_json` string, or null when no preference is stored. */
  raw: string | null;
  /** True once the query has resolved (a null `raw` is then meaningful). */
  loaded: boolean;
  save: (value: unknown) => Promise<void>;
  clear: () => Promise<void>;
} {
  const { db } = usePowerSyncDb();
  const ready = !!orgId && !!userId;

  const { data: rows = [], loaded: queryLoaded } = usePowerSyncQuery<UserUiPreferenceRow>(
    ready ? qUserUiPreference(orgId, userId, preferenceKey) : qNoOp('user-ui-preference'),
  );
  const row = rows[0] ?? null;

  const save = useCallback(
    async (value: unknown) => {
      if (!db || !ready) return;
      // Deterministic id keyed by (org, user, preference) — matches the unique
      // index, so a save from a second device updates the same row instead of
      // inserting a duplicate.
      const id = userUiPreferenceId(orgId, userId, preferenceKey);
      const json = JSON.stringify(value);
      const now = new Date().toISOString();
      // PowerSync client tables are id-keyed VIEWS over ps_data — `ON CONFLICT`
      // raises "cannot UPSERT a view", so INSERT first and fall back to UPDATE
      // on the PK collision. Same pattern as the patient_recent_views write in
      // PatientShellLayout; a check-then-insert would race instead.
      try {
        await db.execute(
          `INSERT INTO user_ui_preferences
             (id, org_id, user_id, preference_key, value_json, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?)`,
          [id, orgId, userId, preferenceKey, json, now, now],
        );
      } catch {
        await db.execute(
          `UPDATE user_ui_preferences SET value_json = ?, updated_at = ? WHERE id = ?`,
          [json, now, id],
        );
      }
    },
    [db, ready, orgId, userId, preferenceKey],
  );

  const clear = useCallback(async () => {
    if (!db || !ready) return;
    await db.execute(
      `DELETE FROM user_ui_preferences
        WHERE org_id = ? AND user_id = ? AND preference_key = ?`,
      [orgId, userId, preferenceKey],
    );
  }, [db, ready, orgId, userId, preferenceKey]);

  return useMemo(
    () => ({ raw: row?.value_json ?? null, loaded: ready && queryLoaded, save, clear }),
    [row?.value_json, ready, queryLoaded, save, clear],
  );
}
